import React, { useState, useEffect, ReactNode } from 'react';

import Button, { ButtonProps } from 'components/button/Button';
import Collapse from 'components/collapse/Collapse';

import { IAccordion } from 'source/NFR.types';

// This component passes a Button, a Collapse, the open state and a toggle function through render props.
// The Button already has a click handler and toggles between labels (depending on expanded/collapsed state), but supports additional props.
// Collapsible content should be wrapped in the passed Collapse component.

interface CollapseProps {
  className?: string;
  children: ReactNode;
  closedHeight?: number;
}

interface AccordionRenderProps {
  Button: React.ComponentType<ButtonProps>;
  Collapse: React.ComponentType<CollapseProps>;
  isOpen: boolean;
  toggle: () => void;
}

export interface AccordionProps extends IAccordion {
  animate?: boolean;
  children?: (renderProps: AccordionRenderProps) => ReactNode;
  expandOnMount?: (width: number) => boolean;
  showButtonText?: boolean;
  buttonTextForLabel?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  animate = true,
  children,
  collapseLabel,
  expandLabel,
  expandOnMount = () => false,
  guid,
  initiallyOpen = false,
  showButtonText,
  buttonTextForLabel
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  useEffect(() => {
    setIsOpen(prev => expandOnMount(window.innerWidth) || prev);
  }, [expandOnMount]);

  const toggle = (): void => {
    setIsOpen(prev => !prev);
  };

  const onClick = (e: React.MouseEvent): void => {
    // Stop propagation so that the 'toggle' method isn't called twice when used on a parent element of the button
    e.stopPropagation();
    toggle();
  };

  // Internal helper component - not exported
  // eslint-disable-next-line react/no-multi-comp
  const AccordionButton = (props: ButtonProps): JSX.Element => {
    const label = isOpen ? collapseLabel : expandLabel;
    const buttonText = buttonTextForLabel ? `${label} ${buttonTextForLabel}` : label;

    return (
      <Button
        aria-controls={guid}
        aria-expanded={isOpen}
        onClick={onClick}
        text={showButtonText ? buttonText : undefined}
        title={buttonText}
        {...props}
      />
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const AccordionCollapse: React.FC<CollapseProps> = ({ className, children, closedHeight }): JSX.Element =>
    animate ? (
      <Collapse className={className} componentProps={{ id: guid }} isOpen={isOpen} closedHeight={closedHeight}>
        {children}
      </Collapse>
    ) : (
      <div className={className} id={guid}>
        {isOpen && children}
      </div>
    );

  if (!children) return null;

  return children({
    Button: AccordionButton,
    Collapse: AccordionCollapse,
    isOpen,
    toggle
  });
};

Accordion.displayName = 'Accordion';

export default Accordion;
